import { CONFIDENCE_LABEL, type Confidence } from '@/content/severance-atlas/coverage'
import type { BaselineRule } from '@/content/severance-atlas/data'
import { STATE_BY_CODE } from '@/content/severance-atlas/model'
import type { ReactNode } from 'react'

/** Heat ramp from the Pelgo blue palette: darker means a stronger obligation or protection. */
export const HEAT: Record<number, string> = {
  3: 'bg-pelgo-blue-dark text-white',
  2: 'bg-[#0050e6] text-white',
  1: 'bg-pelgo-blue-light/50 text-pelgo-blue-dark',
  0: 'bg-gray-100 text-gray-500',
}

export const eyebrowClass = 'text-sm font-semibold uppercase tracking-wide text-pelgo-blue-light'
export const cardClass = 'rounded-2xl border border-gray-200 bg-white shadow-sm'
export const pillBase = 'rounded-full border px-4 py-2 text-sm font-medium transition-colors'

export const scrollToId = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })

export const pip = (v: number) => (
  <span
    className={`mr-2 inline-block h-2.5 w-2.5 rounded-sm align-baseline ${HEAT[v] ?? HEAT[0]}`}
  />
)

export function SectionHead({
  eyebrow,
  title,
  children,
}: { eyebrow: string; title: string; children?: ReactNode }) {
  return (
    <div className="mb-8 max-w-3xl space-y-3">
      <p className={eyebrowClass}>{eyebrow}</p>
      <div className="h-3 w-12 rounded-full bg-pelgo-blue-light" aria-hidden />
      <h2 className="text-3xl font-display font-medium tracking-[-0.6px] text-gray-900">{title}</h2>
      {children ? <p className="text-base text-gray-500 leading-relaxed">{children}</p> : null}
    </div>
  )
}

export function Pill({
  active,
  onClick,
  children,
}: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`${pillBase} ${
        active
          ? 'border-pelgo-blue-dark bg-pelgo-blue-dark text-white'
          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  )
}

export function StateChip({ code, onOpen }: { code: string; onOpen: (code: string) => void }) {
  return (
    <button
      type="button"
      title={STATE_BY_CODE[code]?.n}
      onClick={() => onOpen(code)}
      className="rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-semibold text-gray-900 transition-colors hover:border-[#0050e6] hover:text-[#0050e6]"
    >
      {code}
    </button>
  )
}

export function Badge({
  tone,
  children,
}: { tone: 'hot' | 'warm' | 'flag' | 'plain'; children: ReactNode }) {
  const tones = {
    hot: 'bg-pelgo-blue-dark text-white',
    warm: 'bg-pelgo-blue-light/30 text-pelgo-blue-dark',
    flag: 'bg-amber-100 text-amber-900',
    plain: 'bg-gray-100 text-gray-600',
  }
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  )
}

const CONFIDENCE_STYLE: Record<Confidence, string> = {
  v: 'bg-pelgo-blue-light/30 text-pelgo-blue-dark',
  s: 'bg-gray-100 text-gray-600',
  x: 'bg-amber-100 text-amber-900',
}

export function ConfidenceTag({ c }: { c: Confidence }) {
  return (
    <span
      className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${CONFIDENCE_STYLE[c]}`}
    >
      {CONFIDENCE_LABEL[c]}
    </span>
  )
}

export function RuleCard({ r }: { r: BaselineRule }) {
  return (
    <article className={`${cardClass} grid content-start gap-3 p-6`}>
      <p className="text-xs font-semibold tracking-wide text-[#0050e6]">{r.law}</p>
      <h3 className="text-lg font-display font-medium leading-snug tracking-[-0.3px] text-gray-900">
        {r.h}
      </h3>
      <ul className="m-0 list-disc space-y-1.5 pl-5 text-sm text-gray-700">
        {r.pts.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
      {r.but ? (
        <p className="border-t border-dashed border-gray-200 pt-3 text-sm text-gray-500">{r.but}</p>
      ) : null}
    </article>
  )
}

export function RuleGrid({ rules }: { rules: BaselineRule[] }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {rules.map((r) => (
        <RuleCard key={r.h} r={r} />
      ))}
    </div>
  )
}

export function Callout({ children }: { children: ReactNode }) {
  return (
    <p className="mb-6 max-w-4xl rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
      {children}
    </p>
  )
}

export function SubHead({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-4 mt-12 text-xl font-display font-medium tracking-[-0.3px] text-gray-900 first:mt-0">
      {children}
    </h3>
  )
}
